/**
 * App Component with Enhanced SAML Integration
 *
 * This component provides comprehensive SAML authentication handling including:
 * - Authentication state management
 * - SAML callback processing
 * - Auth Hub event listening
 * - Token refresh handling
 * - Route protection based on authentication
 *
 * SAML Features:
 * - Automatic authentication state checking
 * - Auth Hub event monitoring
 * - Token refresh monitoring
 * - Enhanced error handling and logging
 * - Integration with existing Redux state management
 */

import React, { useEffect, useState, useRef } from "react";
import "./App.css";
import { Router, Route, Switch, Redirect } from "react-router-dom";
import LoginComponent from "./components/feature/login/login";
import Layout from "./components/layout/layout";
import AdminLayout from "./components/layout/adminLayout";
import EtlLayout from "./components/layout/etlLayout";
import AwvLayout from "./components/layout/awvLayout";
import MainAweLayout from "./components/layout/MainAweLayout";
import HomeComponent from "./components/home/home";
import Terms from "./components/feature/Terms/terms";
import history from "./history";
import { signIn, permssionAssign } from "./store/actions";
import { connect } from "react-redux";
import LogoutModel from "./components/Dialog/LogoutModel";
import { getSecretManager } from "../src/Pages/config/SecretManager";
import AccessKeys from "../src/assets/constants/AccessKeyAPI.Instance";
import { LogFileUploadToS3 } from "./assets/constants/LogFileUploadToS3";
import { Auth, Hub } from "aws-amplify";
import adminApi from "./assets/constants/AdminRafservice.Instance";
import axios from "axios";
import CircularProgress from '@mui/material/CircularProgress';
// SAML Fix: Import token exchange lock handler
import { initiateSAMLCallback } from './utils/samlCallbackHandler';

// Add a global error boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    // You can log error info here if needed
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="error-container">
          <h2>Application Error</h2>
          <div
            className="error-message"
            style={{ color: "red", marginTop: "20px" }}
          >
            <p>
              {this.state.error?.message ||
                "A fatal error occurred. Please refresh the page."}
            </p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const App = (props) => {
  const [logMessages, setLogMessages] = useState([]);
  const logMessagesRef = useRef([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const samlProcessingRef = useRef(false);
  const [samlProcessing, setSamlProcessing] = useState(false); // New state for SAML processing

  // Helper to add detailed log entries
  const logEvent = (message, details) => {
    setLogMessages((prev) => {
      const detailString = details !== undefined ? ' ' + JSON.stringify(details) : '';
      const updated = [...prev, `[${new Date().toISOString()}] ${message}${detailString}`];
      logMessagesRef.current = updated;
      return updated;
    });
  };

  const checkAuthState = async () => {
    try {
      setIsLoading(true);
      logEvent("Checking authentication state");
      const user = await Auth.currentAuthenticatedUser();
      console.log("Current authenticated user:", user);
      const isAuth = !!user;
      setIsAuthenticated(isAuth);
      logEvent(
        `Authentication state: ${
          isAuth ? "Authenticated" : "Not authenticated"
        }`
      );
    } catch (error) {
      logEvent(`No authenticated user: ${error.message}`);
      console.log("No authenticated user:", error);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTokenRefresh = async () => {
    try {
      logEvent("Attempting to refresh tokens");
      const currentUser = await Auth.currentAuthenticatedUser();
      const session = await Auth.currentSession();

      // Tokens are automatically refreshed by Amplify
      const newIdToken = session.getIdToken().getJwtToken();
      const newAccessToken = session.getAccessToken().getJwtToken();

      // Update stored tokens
      localStorage.setItem("accessToken", newAccessToken);
      localStorage.setItem("idToken", newIdToken);
      localStorage.setItem("Token", newAccessToken);

      logEvent("Tokens refreshed successfully");
    } catch (error) {
      logEvent(`Token refresh error: ${error.message}`);
      console.error("Token refresh error:", error);

      // Handle token refresh failure
      setAuthError("Session expired. Please log in again.");
      setIsAuthenticated(false);

      // Clear stored tokens on refresh failure
      localStorage.removeItem("accessToken");
      localStorage.removeItem("idToken");
      localStorage.removeItem("Token");
      localStorage.removeItem("user");

      // Redirect to login
      setTimeout(() => {
        history.push("/");
      }, 2000);
    }
  };

  useEffect(() => {
    logEvent("App mounted");
    document
      .getElementsByTagName("body")[0]
      .classList.add("normal-layout-container");
    document
      .getElementsByTagName("body")[0]
      .classList.remove("awe-layout-container");
  }, []);

  // ✅ SAML Fix: Process SAML callback with token exchange lock
  useEffect(() => {
    const processSAMLCallback = async () => {
      try {
        logEvent("Checking for SAML callback authorization code...");
        const result = await initiateSAMLCallback();
        if (result) {
          logEvent("SAML callback processed successfully");
        }
      } catch (error) {
        logEvent(`SAML callback processing failed: ${error.message}`);
        console.error('[App] SAML callback error:', error);
      }
    };

    processSAMLCallback();
  }, []); // Run once on app mount

  // Patch: Add a fallback timeout to ensure loading never hangs forever
  useEffect(() => {
    if (!isLoading) return; // Only set timer if still loading
    const timer = setTimeout(() => {
      if (!isAuthenticated && isLoading) {
        setAuthError("Authentication timed out. Please try logging in again.");
        setIsLoading(false);
      }
    }, 20000); // 20 seconds fallback
    return () => clearTimeout(timer);
  }, [isAuthenticated, isLoading]);

  // Patch: Add catch-all error handling for async auth logic
  const safeCheckAuthState = async () => {
    try {
      await checkAuthState();
    } catch (error) {
      setAuthError("Unexpected error during authentication: " + error.message);
      setIsLoading(false);
    }
  };
  // Replace checkAuthState() with safeCheckAuthState() in useEffect
  useEffect(() => {
    const authMethod = localStorage.getItem("authMethod");
    logEvent("Authentication method detected", { authMethod });
    
    if (authMethod === "SAML") {
      logEvent("SAML authentication flow initiated");
      // SAML: check localStorage for user and tokens
      const user = localStorage.getItem("user");
      const token = localStorage.getItem("Token");
      const controlPermissions = localStorage.getItem('controlPermissions');
      const controlUser = localStorage.getItem('controlUser');
      
      logEvent("SAML stored data check", { 
        hasUser: !!user, 
        hasToken: !!token, 
        hasPermissions: !!controlPermissions,
        hasControlUser: !!controlUser,
        tokenLength: token ? token.length : 0
      });
      
      if (user && token) {
        logEvent("SAML authentication: User and token found, proceeding with authentication");
        setIsAuthenticated(true);
        setIsLoading(false);
        
        // Restore Redux state from localStorage
        try {
          const userObj = JSON.parse(user);
          const permissions = controlPermissions ? JSON.parse(controlPermissions) : null;
          
          logEvent("SAML state restoration", {
            userEmail: userObj?.userEmail,
            hasUserData: !!userObj,
            permissionsCount: permissions ? permissions.length : 0,
            authMethod: userObj?.authMethod
          });
          
          if (userObj) {
            props.signIn(userObj);
            logEvent("SAML Redux user state updated", { userEmail: userObj.userEmail });
          }
          if (permissions) {
            props.permssionAssign(permissions);
            logEvent("SAML Redux permissions updated", { permissionsCount: permissions.length });
          }
        } catch (e) { 
          logEvent("SAML state restoration error", { error: e.message });
          originalConsole.error("SAML state restoration error:", e);
        }
        
        logEvent("SAML authentication completed successfully");
        
        // Upload logs for already authenticated SAML user
        setTimeout(() => {
          uploadLogsToS3("SAML_EXISTING_SESSION_Log");
        }, 1000); // Delay to ensure all authentication processes complete
        
      } else {
        logEvent("SAML authentication: Missing user or token, setting unauthenticated");
        setIsAuthenticated(false);
        setIsLoading(false);
      }
      return; // End useEffect early for SAML
    } else {
      logEvent("Amplify authentication flow initiated");
      // Username/password (Amplify) logic
      Auth.currentAuthenticatedUser()
        .then(user => {
          logEvent("Amplify authentication successful", { 
            username: user?.username,
            hasAttributes: !!user?.attributes
          });
          setIsAuthenticated(true);
          setIsLoading(false);
          // Restore Redux state from localStorage if available
          try {
            const userStr = localStorage.getItem('user');
            const permissionsStr = localStorage.getItem('controlPermissions');
            if (userStr) {
              const userData = JSON.parse(userStr);
              props.signIn(userData);
              logEvent("Amplify Redux user state restored", { userEmail: userData?.userEmail });
            }
            if (permissionsStr) {
              const permissions = JSON.parse(permissionsStr);
              props.permssionAssign(permissions);
              logEvent("Amplify Redux permissions restored", { permissionsCount: permissions.length });
            }
          } catch (e) { 
            logEvent("Amplify state restoration error", { error: e.message });
            originalConsole.error("Amplify state restoration error:", e);
          }
        
        // Upload logs for authenticated Amplify user
        setTimeout(() => {
          uploadLogsToS3("AMPLIFY_SESSION_Log");
        }, 1000);
        
        })
        .catch((error) => {
          logEvent("Amplify authentication failed", { error: error.message });
          setIsAuthenticated(false);
          setIsLoading(false);
        });
    }
  }, []);

  useEffect(() => {
    const unlisten = history.listen((location, action) => {
      logEvent(`Route changed to ${location.pathname} (${action})`);
    });
    return () => unlisten();
  }, []);

  // Preserve console functionality for debugging while adding comprehensive logging
  const originalConsole = {
    log: console.log,
    error: console.error,
    warn: console.warn,
    info: console.info
  };


  React.useEffect(() => {
    // Enhance console methods to collect logs while preserving original functionality
    const origLog = originalConsole.log;
    const origError = originalConsole.error;
    const origWarn = originalConsole.warn;
    const origInfo = originalConsole.info;
    
    const logs = [];
    
    // Enhanced console.log with detailed logging
    console.log = (...args) => {
      const logMessage = "[LOG] " + args.map(String).join(" ");
      logs.push(logMessage);
      logEvent(logMessage);
      origLog.apply(console, args); // Preserve original functionality
    };
    
    // Enhanced console.error with detailed logging
    console.error = (...args) => {
      const errorMessage = "[ERROR] " + args.map(String).join(" ");
      logs.push(errorMessage);
      logEvent(errorMessage);
      origError.apply(console, args); // Preserve original functionality
    };
    
    // Enhanced console.warn with detailed logging
    console.warn = (...args) => {
      const warnMessage = "[WARN] " + args.map(String).join(" ");
      logs.push(warnMessage);
      logEvent(warnMessage);
      origWarn.apply(console, args); // Preserve original functionality
    };
    
    // Enhanced console.info with detailed logging
    console.info = (...args) => {
      const infoMessage = "[INFO] " + args.map(String).join(" ");
      logs.push(infoMessage);
      logEvent(infoMessage);
      origInfo.apply(console, args); // Preserve original functionality
    };
    
    // Window error handler
    const windowErrorHandler = (e) => {
      const windowErrorMessage = "[WINDOW ERROR] " + e.message + " at " + e.filename + ":" + e.lineno;
      logs.push(windowErrorMessage);
      logEvent(windowErrorMessage);
    };
    
    window.addEventListener("error", windowErrorHandler);
    setLogMessages((prev) => [...prev, ...logs]);
    
    return () => {
      // Restore original console methods
      console.log = origLog;
      console.error = origError;
      console.warn = origWarn;
      console.info = origInfo;
      window.removeEventListener("error", windowErrorHandler);
    };
  }, []);

  // Helper function to upload logs to S3
  const uploadLogsToS3 = async (logSuffix = "AppPageLog") => {
    try {
      logEvent("Preparing to upload logs to S3", { logSuffix });
      
      // Check if we have a valid token first
      const token = localStorage.getItem("Token") || localStorage.getItem("accessToken");
      if (!token) {
        logEvent("No token available for /keys API call, skipping S3 upload");
        return;
      }
      
      logEvent("Token found, calling /keys API for S3 upload", { 
        hasToken: !!token, 
        tokenLength: token.length 
      });
      
      const res = await AccessKeys.get("/keys");
      
      if (res && res.data && res.data.access_key && res.data.secret_key) {
        logEvent("Successfully fetched access keys from API");
        originalConsole.log("AccessKeys API response:", res.data);
        
        const secrets = await getSecretManager(
          res.data.access_key,
          res.data.secret_key,
          res.data.session_token,
          false
        );
        
        logEvent("Successfully fetched secrets from getSecretManager");
        const BUCKET = process.env.REACT_APP_LOG_BUCKET;
        
        LogFileUploadToS3(
          logMessagesRef.current, // always the latest logs
          secrets.access_key,
          secrets.secret_key,
          BUCKET,
          logSuffix
        );
        
        logEvent("Log file uploaded to S3 successfully", { bucket: BUCKET, suffix: logSuffix });
        originalConsole.log("S3 upload completed for:", logSuffix);
        
      } else {
        logEvent("AccessKeys API did not return expected keys", { 
          response: res ? res.data : "no response",
          hasData: !!(res && res.data),
          hasAccessKey: !!(res && res.data && res.data.access_key),
          hasSecretKey: !!(res && res.data && res.data.secret_key)
        });
        originalConsole.error("AccessKeys API response missing required keys:", res);
      }
    } catch (error) {
      logEvent("Error during S3 upload process", { 
        error: error.message,
        stack: error.stack
      });
      originalConsole.error("S3 upload error:", error);
    }
  };

  React.useEffect(() => {
    // On full page load, just log the event - don't upload logs yet
    const handleWindowLoad = () => {
      logEvent("Window loaded - waiting for authentication to complete before S3 upload");
    };
    
    window.addEventListener("load", handleWindowLoad);
    
    return () => {
      window.removeEventListener("load", handleWindowLoad);
    };
  }, []);

  // Add a global guard for SAML callback handling
  let samlCallbackHandled = false;

  // SAML callback handling function
  const handleSAMLCallback = async (code) => {
    setIsLoading(true);
    setSamlProcessing(true); // Start SAML processing
    if (samlCallbackHandled) {
      logEvent("SAML callback handler already ran, skipping duplicate call.");
      setSamlProcessing(false);
      return;
    }
    samlCallbackHandled = true;
    logEvent("SAML callback handler running with code: " + code);
    const urlParams = new URLSearchParams(window.location.search);
    const codeURL = urlParams.get("code");
    logEvent("SAML callback code from URL: " + codeURL);

    if (!code) return;

    try {
      // Build token request
      const domain = process.env.REACT_APP_COGNITO_DOMAIN;
      const region = process.env.REACT_APP_AWS_REGION || "us-west-2";
      const cognitoDomain = domain.startsWith("https://")
        ? domain
        : `https://${domain}`;
      const callbackRedirectUri =
        process.env.REACT_APP_REDIRECT_URI || window.location.origin;
      const headers = { "Content-Type": "application/x-www-form-urlencoded" };
      const requestBody = {
        grant_type: "authorization_code",
        code,
        redirect_uri: callbackRedirectUri,
        client_id: process.env.REACT_APP_COGNITO_CLIENT_ID,
      };
      // Only add client_secret if it exists
      if (process.env.REACT_APP_COGNITO_CLIENT_SECRET) {
        requestBody.client_secret = process.env.REACT_APP_COGNITO_CLIENT_SECRET;
      }
      const tokenUrl = `${cognitoDomain}/oauth2/token`;
      logEvent("SAML Token Request");

      const tokenResponse = await axios.post(
        tokenUrl,
        new URLSearchParams(requestBody),
        { headers }
      );
      const tokens = tokenResponse.data;
      logEvent("SAML Token Response:", tokens);
      if (!tokens.id_token || !tokens.access_token) {
        logEvent("Token response missing id_token or access_token", tokens);
        throw new Error("Token response missing id_token or access_token");
      }
      // Decode JWT to get user information
      const userInfo = JSON.parse(atob(tokens.id_token.split(".")[1]));
      logEvent("Decoded user info from id_token", userInfo);
      console.log("Decoded user info from id_token:", userInfo.identities[0].userId);
      const userObject = {
        userEmail: userInfo.email,
        userSub: userInfo.sub,
        data: {
          idToken: tokens.id_token,
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token || "",
        },
        authMethod: "SAML",
        loginTime: new Date().toISOString(),
        isSignedIn: true,
        userId: `${userInfo.identities[0].userId}`,
        userName: userInfo['cognito:username'] || userInfo.email
      };
      logEvent("SAML tokens:", tokens);
      // Store in localStorage with detailed logging
      logEvent("Storing SAML authentication data in localStorage", {
        userEmail: userInfo.email,
        userSub: userInfo.sub,
        hasAccessToken: !!tokens.access_token,
        hasIdToken: !!tokens.id_token,
        hasRefreshToken: !!tokens.refresh_token,
        accessTokenLength: tokens.access_token ? tokens.access_token.length : 0
      });
      
      localStorage.setItem("user", JSON.stringify(userObject));
      localStorage.setItem("Token", tokens.access_token);
      localStorage.setItem("idToken", tokens.id_token);
      localStorage.setItem("accessToken", tokens.access_token);
      localStorage.setItem("refreshToken", tokens.refresh_token || "");
      localStorage.setItem("authMethod", "SAML");
      localStorage.setItem("userEmail", userInfo.email);
      localStorage.setItem("userSub", userInfo.sub);
      
      logEvent("SAML localStorage storage completed", {
        storedKeys: ["user", "Token", "idToken", "accessToken", "refreshToken", "authMethod", "userEmail", "userSub"]
      });
      const loginData = {
        email: userInfo.email,
        sub: userInfo.sub,
        userId: `${userInfo.identities[0].userId}`,
        userName: userInfo['cognito:username'] || userInfo.email,
        organizationId: 1
       // password: "", // Password is not used in SAML, but API might require it
      };
      logEvent("Preparing /auth/signin API request", {
        endpoint: "/auth/signin",
        requestData: loginData,
        hasAccessToken: !!tokens.access_token,
        accessTokenLength: tokens.access_token ? tokens.access_token.length : 0
      });
      
      // Log the actual API call
      originalConsole.log("SAML /auth/signin API call initiated with data:", loginData);
      
      const response = await adminApi.post(
        "/auth/signin",
       loginData,
        {
          headers: {
            Authorization: `Bearer ${tokens.access_token}`,
          },
        }
      );
      
      logEvent("Backend /auth/signin API response received", {
        status: response.status,
        hasData: !!response.data,
        hasPermissions: !!response.data?.permissions,
        hasUserDetails: !!response.data?.userDetails,
        permissionsCount: response.data?.permissions ? response.data.permissions.length : 0,
        userDetailsKeys: response.data?.userDetails ? Object.keys(response.data.userDetails) : []
      });
      
      originalConsole.log("SAML /auth/signin API response:", response.data);
      const permissions = response.data.permissions;
      const userDetails = response.data.userDetails;
      
      logEvent('User permissions and details extracted from API response', { 
        permissionsCount: permissions ? permissions.length : 0,
        userDetailsKeys: userDetails ? Object.keys(userDetails) : [],
        hasPermissions: !!permissions,
        hasUserDetails: !!userDetails
      });
      
      // Store permissions and user details with detailed logging
      localStorage.setItem("controlPermissions", JSON.stringify(permissions));
      localStorage.setItem("controlUser", JSON.stringify(userDetails));
      localStorage.setItem("controlAccessToken", tokens.access_token);
      
      logEvent('User permissions and details stored in localStorage', { 
        permissionsStored: permissions ? permissions.length : 0,
        userDetailsStored: userDetails ? Object.keys(userDetails).length : 0,
        controlAccessTokenLength: tokens.access_token ? tokens.access_token.length : 0
      });
      
      originalConsole.log("SAML permissions stored:", permissions);
      originalConsole.log("SAML user details stored:", userDetails);
      logEvent("token url ", tokenUrl);
      logEvent("body: ", requestBody);

      // Update Redux state
      props.permssionAssign(permissions);
      
      // Update Redux state
      props.signIn(userObject);
      logEvent("Redux userDetails updated", userObject);
      setIsAuthenticated(true);
      setIsLoading(false);
      setSamlProcessing(false); // End SAML processing
      logEvent("SAML authentication process complete, redirecting to /home");
      
      // Upload successful SAML authentication logs to S3 after token is stored
      logEvent("Initiating S3 upload after successful SAML authentication");
      uploadLogsToS3("SAML_SUCCESS_Log");
      
      // Clean up URL and redirect
      window.history.replaceState({}, document.title, window.location.pathname);
      history.push("/home");
    } catch (error) {
      if (error.response) {
        logEvent("SAML callback error response", {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          headers: error.response.headers
        });
        originalConsole.error("SAML callback error response:", error.response.data);
      }
      
      // Always log the code and error info on failure
      logEvent("SAML authentication error (failure/redirect)", {
        code,
        errorMessage: error.message,
        errorStack: error.stack,
        errorResponse: error.response ? error.response.data : undefined,
        url: window.location.href,
        timestamp: new Date().toISOString()
      });
      
      originalConsole.error("SAML authentication failed:", error);
      
      // Upload error logs to S3 immediately (use any available token)
      logEvent("Initiating S3 upload for SAML authentication error");
      uploadLogsToS3("SAML_ERROR_Log");
      
      setAuthError(
        `Authentication failed: ${error.message}. Please try logging in again.`
      );
      setIsAuthenticated(false);
      setIsLoading(false);
      setSamlProcessing(false); // End SAML processing on error
      // Redirect to login page after logging
      setTimeout(() => {
        history.push("/");
      }, 2000);
    }
  };

  useEffect(() => {
    logEvent('SAML code check useEffect running', { url: window.location.href });
    const urlParams = new URLSearchParams(window.location.search);
    logEvent("Checking for SAML callback in URL before handleSAMLCallBack: ", urlParams.get("code"));
    if (urlParams.get("code")) {
      handleSAMLCallback(urlParams.get("code"));
    } else {
      logEvent('No SAML code found in URL', { url: window.location.href });
    }
  }, []);

  // All hooks must be at the top level, never inside conditionals or after returns
  useEffect(() => {
    // Check Redux first, then localStorage
    if (props.userDetails && props.userDetails.isSignedIn) {
      // Only redirect to /home if authenticated
      if (window.location.pathname === "/" || window.location.pathname === "/login") {
        history.push('/home');
      }
      return;
    }
    const user = localStorage.getItem('user');
    const authMethod = localStorage.getItem('authMethod');
    if (user && authMethod === 'SAML') {
      // Only redirect to /home if authenticated
      if (window.location.pathname === "/" || window.location.pathname === "/login") {
        history.push('/home');
      }
    } else {
      // If not authenticated, always redirect to login
     /*  if (window.location.pathname !== "/" && window.location.pathname !== "/login") {
        history.push('/');
      } */
    }
  }, [props.userDetails]);

  // Restore the loading state render block at the top of the render logic
  if (isLoading || samlProcessing) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: 'rgba(255,255,255,0.95)',
        zIndex: 9999
      }}>
        <CircularProgress size={60} thickness={5} style={{ color: '#21518b' }} />
        <div style={{ marginTop: 24, color: '#21518b', fontWeight: 500, fontSize: 20 }}>
          Loading...
        </div>
      </div>
    );
  }

  if (!isLoading && authError) {
    return (
      <div className="error-container">
        <h2>Authentication Error app</h2>
        <div
          className="error-message"
          style={{ color: "red", marginTop: "20px" }}
        >
          <p>{authError}</p>
        </div>
        <p>Redirecting to login page...</p>
      </div>
    );
  }

  const authenticate = props.userDetails?.isSignedIn || isAuthenticated;

  return (
    <ErrorBoundary>
    <Router history={history}>
      <Switch>
        {authenticate ? (
          <React.Fragment>
            <Route path="/home" component={HomeComponent} />
            <Route path="/layout" component={Layout} />
            <Route path="/admin" component={AdminLayout} />
            <Route path="/etl" component={EtlLayout} />
            <Route path="/awe" component={AwvLayout} />
            <Route path="/new_awe" component={MainAweLayout} />
            <Route path="/" exact>
              <Redirect to="/home" />
            </Route>
            <Route path="*">
              <Redirect to="/home" />
            </Route>
            <LogoutModel />
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Route path="/" exact component={LoginComponent} />
            <Route path="/terms" component={Terms} />
            <Route path="*">
              <Redirect to="/" />
            </Route>
          </React.Fragment>
        )}
      </Switch>
    </Router>
    </ErrorBoundary>
  );
};

const mapStateToProps = (state) => {
  return {
    userDetails: state.auth,
  };
};
export default connect(mapStateToProps, { signIn, permssionAssign })(App);
